import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  tryUnlockFromSearch,
  unlockWebAuth,
  validateWebAuthPin,
} from "../config/webAuth";
import "./LandingPage.css";
import DownloadAppCta from "./DownloadAppCta";

const horizontalLogoSrc = `${process.env.PUBLIC_URL}/${encodeURIComponent("InvestX - Horizontal transparent.png")}`;
const heroAllocationSrc = `${process.env.PUBLIC_URL}/hero-phone-allocation.png?v=2`;
const heroPerformanceSrc = `${process.env.PUBLIC_URL}/hero-phone-performance.png?v=2`;
const homePageIphone16Src = `${process.env.PUBLIC_URL}/${encodeURIComponent("HomePage iPhone 16.png")}`;
const featureHomeSrc = `${process.env.PUBLIC_URL}/${encodeURIComponent("Simulator Screenshot - iPhone 17 Pro - 2026-07-09 at 21.12.11.png")}`;
const featureWatchlistSrc = `${process.env.PUBLIC_URL}/${encodeURIComponent("Simulator Screenshot - iPhone 17 Pro - 2026-07-09 at 21.07.44.png")}`;
const featurePerformanceSrc = `${process.env.PUBLIC_URL}/${encodeURIComponent("Simulator Screenshot - iPhone 17 Pro - 2026-07-09 at 20.25.21.png")}`;
const featureLearningSrc = `${process.env.PUBLIC_URL}/${encodeURIComponent("Simulator Screenshot - iPhone 17 - Learning Portal P:E Ratio.png")}`;
const problemIconAnalyticsSrc = `${process.env.PUBLIC_URL}/problem-icon-analytics.png`;
const problemIconStatisticsSrc = `${process.env.PUBLIC_URL}/problem-icon-statistics.png`;
const problemIconPdfSrc = `${process.env.PUBLIC_URL}/problem-icon-pdf.png`;
const problemIconSpreadsheetSrc = `${process.env.PUBLIC_URL}/problem-icon-spreadsheet.png`;
const problemIconUnlinkSrc = `${process.env.PUBLIC_URL}/problem-icon-unlink.png`;

const heroPhones = [
  {
    id: "allocation",
    src: heroAllocationSrc,
    alt: "InvestX allocation sankey view on iPhone",
    label: "InvestX allocation view on iPhone",
    layer: "front",
  },
  {
    id: "performance",
    src: heroPerformanceSrc,
    alt: "InvestX performance charts on iPhone",
    label: "InvestX performance view on iPhone",
    layer: "mid",
  },
];

/** 0.6s initial wait, then each phone: 1.2s anim + 0.6s gap before the next */
const HERO_PHONE_INITIAL_DELAY_MS = 600;
const HERO_PHONE_ANIM_MS = 1200;
const HERO_PHONE_GAP_MS = 600;
const heroPhoneEnterDelayMs = (index) =>
  HERO_PHONE_INITIAL_DELAY_MS + index * (HERO_PHONE_ANIM_MS + HERO_PHONE_GAP_MS);

const CALIBRI_STACK =
  '"Calibri", "Calibri Light", "Segoe UI", system-ui, sans-serif';

const howItWorksSteps = [
  {
    step: "01",
    title: "Download the app",
    description: "Get InvestX on iOS and create your account in minutes.",
  },
  {
    step: "02",
    title: "Add your portfolio",
    description: "Log holdings and transactions to build a complete picture.",
  },
  {
    step: "03",
    title: "Track and analyze",
    description: "Follow performance, explore charts, and learn the metrics behind every move.",
  },
];

const faqItems = [
  {
    question: "Does InvestX execute trades?",
    answer:
      "No. InvestX is portfolio tracking and analysis software. It does not place trades or provide investment recommendations.",
  },
  {
    question: "Is InvestX free?",
    answer:
      "Yes. InvestX is free to download, and a free plan is available so you can track and analyze your portfolio without a paid subscription.",
  },
  {
    question: "Can I use InvestX on the web?",
    answer:
      "Yes. Existing accounts can sign in on the web. The full experience is also available in the iOS app.",
  },
  {
    question: "How do I set up an account?",
    answer:
      "Download the app, create an account with your email, then add your holdings or import transactions. You can be up and running in a few minutes.",
  },
  {
    question: "What data do I need to get started?",
    answer:
      "Add your positions manually or import transactions. InvestX builds your portfolio view from what you enter.",
  },
  {
    question: "Which markets and currencies are supported?",
    answer:
      "InvestX supports stocks, ETFs, and crypto across major markets. You can track positions in multiple currencies; availability may vary by asset and region.",
  },
  {
    question: "Is my portfolio data secure?",
    answer:
      "Your data is tied to your account and used to power your dashboard. We do not sell your portfolio information. Sign-in is protected, and you can delete your account at any time.",
  },
  {
    question: "Does InvestX connect to my broker?",
    answer:
      "You can add holdings and import transactions yourself. InvestX does not place trades through your broker — it is for tracking and analysis only.",
  },
];

const problemSolutionSection = {
  id: "problem-solution",
  title: "Problem vs Solution",
  problemRingAlt:
    "Broken link in the center of a red ring surrounded by analytics, spreadsheet, PDF, and chart icons",
  problemIcons: [
    { id: "analytics", src: problemIconAnalyticsSrc, angle: 0, tilt: -10 },
    { id: "spreadsheet", src: problemIconSpreadsheetSrc, angle: 90, tilt: 10 },
    { id: "pdf", src: problemIconPdfSrc, angle: 180, tilt: -10 },
    { id: "statistics", src: problemIconStatisticsSrc, angle: 270, tilt: 10 },
  ],
  problemCenterSrc: problemIconUnlinkSrc,
  solutionImageSrc: homePageIphone16Src,
  solutionImageAlt: "InvestX app on iPhone 16",
  problem: [
    "Using multiple apps",
    "Manual spreadsheets",
    "Time-consuming research",
  ],
  solution: [
    "Unified portfolio tracking",
    "AI-powered analysis",
    "Automated insights",
  ],
};

const featureCards = [
  {
    id: "portfolio",
    title: "Portfolio overview at a glance",
    description:
      "Total value, today's movers, and where every dollar sits — one screen, no digging.",
    visualSrc: featureHomeSrc,
    visualAlt: "InvestX home screen showing total portfolio value and daily movers",
  },
  {
    id: "research",
    title: "Research before you buy",
    description:
      "Position size, cost basis, and sector detail on every stock you're watching.",
    visualSrc: featureWatchlistSrc,
    visualAlt: "InvestX watchlist detail view for a single stock position",
  },
  {
    id: "performance",
    title: "Performance, actually explained",
    description:
      "Cash value, daily gain, and volatility charted so trends are obvious, not buried.",
    visualSrc: featurePerformanceSrc,
    visualAlt: "InvestX performance screen with cash value and volatility charts",
  },
  {
    id: "learning",
    title: "Learn the metrics as you go",
    description:
      "Built-in explainers for P/E, sector comparisons, and more — right where you need them.",
    visualSrc: featureLearningSrc,
    visualAlt: "InvestX learning portal explaining the P/E ratio",
  },
];

const Reveal = ({ children, as: Tag = "div", className = "", delay = 0, ...rest }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -10% 0px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref}
      className={`reveal${visible ? " reveal--visible" : ""}${className ? ` ${className}` : ""}`}
      style={delay ? { "--reveal-delay": `${delay}ms` } : undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
};

const LandingPage = ({ user, children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const isLanding = !user && location.pathname === "/";
  const publicShell = !user;
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");

  const openPinModal = () => {
    setPinValue("");
    setPinError("");
    setShowPinModal(true);
  };

  const closePinModal = () => {
    setShowPinModal(false);
    setPinValue("");
    setPinError("");
  };

  const submitPin = (event) => {
    event.preventDefault();
    if (!validateWebAuthPin(pinValue.trim())) {
      setPinError("Invalid code.");
      return;
    }
    unlockWebAuth();
    closePinModal();
    navigate("/login");
  };

  useEffect(() => {
    if (!publicShell) return undefined;
    document.documentElement.classList.add("landing-cobalt-root");
    return () => document.documentElement.classList.remove("landing-cobalt-root");
  }, [publicShell]);

  useEffect(() => {
    if (tryUnlockFromSearch(location.search)) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        unlockWebAuth();
        navigate("/login");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [navigate]);

  return (
    <div
      className={
        publicShell
          ? "landing-cobalt landing-cobalt--shell"
          : undefined
      }
      style={{
        fontFamily: CALIBRI_STACK,
        ...(publicShell ? {} : { minHeight: "100vh", position: "relative" }),
      }}
    >
      {publicShell && (
        <header className="landing-cobalt-nav" aria-label="Marketing">
          <div className="landing-cobalt-nav__inner">
            <button
              type="button"
              className="landing-cobalt-nav__brand"
              onClick={() => navigate("/")}
              aria-label="InvestX home"
            >
              <img src={horizontalLogoSrc} alt="InvestX" />
            </button>
            <DownloadAppCta className="landing-cobalt-nav__cta" />
          </div>
        </header>
      )}

      {showPinModal && (
        <div
          className="landing-admin-pin"
          role="presentation"
          onClick={closePinModal}
        >
          <form
            className="landing-admin-pin__dialog"
            role="dialog"
            aria-labelledby="admin-pin-title"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
            onSubmit={submitPin}
          >
            <h2 id="admin-pin-title" className="landing-admin-pin__title">
              Admin access
            </h2>
            <label className="landing-admin-pin__label" htmlFor="admin-pin-input">
              Enter code
            </label>
            <input
              id="admin-pin-input"
              className="landing-admin-pin__input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus
              value={pinValue}
              onChange={(event) => {
                setPinValue(event.target.value);
                if (pinError) setPinError("");
              }}
            />
            {pinError ? (
              <p className="landing-admin-pin__error" role="alert">
                {pinError}
              </p>
            ) : null}
            <div className="landing-admin-pin__actions">
              <button
                type="button"
                className="landing-admin-pin__btn landing-admin-pin__btn--ghost"
                onClick={closePinModal}
              >
                Cancel
              </button>
              <button type="submit" className="landing-admin-pin__btn">
                Continue
              </button>
            </div>
          </form>
        </div>
      )}

      {isLanding ? (
        <main>
          <section className="landing-v2-hero" aria-labelledby="hero-title">
            <div className="landing-v2-hero__ambient" aria-hidden />
            <div className="landing-page-width landing-page-width--ruled">
              <div className="landing-v2-hero__grid">
                <div className="landing-v2-hero__copy">
                  <h1 id="hero-title" className="landing-v2-hero__title">
                    See your <span className="landing-v2-hero__title-accent">money</span> clearly
                  </h1>
                  <p className="landing-v2-hero__subtitle">
                    One dashboard for stocks, crypto, and cash — tracked, charted, and
                    explained without the spreadsheets.
                  </p>
                  <div className="landing-v2-hero__cta">
                    <DownloadAppCta className="landing-cobalt-pill landing-cobalt-pill--primary" />
                    <p className="landing-v2-hero__meta">
                      Free to download · Available on iOS
                    </p>
                  </div>
                </div>
                <div className="landing-v2-phone">
                  <div className="landing-v2-phone__glow" aria-hidden />
                  <div className="landing-v2-phone__stack">
                    {heroPhones.map((phone, index) => (
                      <div
                        key={phone.id}
                        className={`landing-v2-phone__device landing-v2-phone__device--${phone.layer}`}
                        style={{ "--phone-enter-delay": `${heroPhoneEnterDelayMs(index)}ms` }}
                        aria-label={phone.label}
                      >
                        <img
                          className="landing-v2-phone__screen"
                          src={phone.src}
                          alt={phone.alt}
                          draggable="false"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            id={problemSolutionSection.id}
            className="landing-v2-problem-solution"
            aria-labelledby="problem-solution-title"
          >
            <div className="landing-page-width landing-page-width--ruled">
              <h2 id="problem-solution-title" className="landing-v2-problem-solution__title">
                {problemSolutionSection.title}
              </h2>
              <div className="landing-v2-problem-solution__grid">
                <Reveal as="div" className="landing-v2-problem-solution__item">
                  <div
                    className="landing-v2-problem-solution__visual landing-v2-problem-solution__visual--ring"
                    role="img"
                    aria-label={problemSolutionSection.problemRingAlt}
                  >
                    <div className="landing-v2-problem-ring landing-v2-problem-ring--danger" aria-hidden>
                      <div className="landing-v2-problem-ring__glow" />
                      <div className="landing-v2-problem-ring__circle" />
                      <img
                        className="landing-v2-problem-ring__center"
                        src={problemSolutionSection.problemCenterSrc}
                        alt=""
                        draggable="false"
                      />
                      {problemSolutionSection.problemIcons.map((icon) => (
                        <div
                          key={icon.id}
                          className="landing-v2-problem-ring__icon"
                          style={{
                            "--icon-angle": `${icon.angle}deg`,
                            "--icon-tilt": `${icon.tilt}deg`,
                          }}
                        >
                          <img src={icon.src} alt="" draggable="false" />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="landing-v2-problem-solution__col landing-v2-problem-solution__col--problem">
                    <div className="landing-v2-problem-solution__body">
                      <h3 className="landing-v2-problem-solution__label">Problem</h3>
                      <ul className="landing-v2-problem-solution__list">
                        {problemSolutionSection.problem.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
                <Reveal as="div" className="landing-v2-problem-solution__item" delay={120}>
                  <div className="landing-v2-problem-solution__visual landing-v2-problem-solution__visual--solution">
                    <div className="landing-v2-problem-ring landing-v2-problem-ring--success" aria-hidden>
                      <div className="landing-v2-problem-ring__glow" />
                      <div className="landing-v2-problem-ring__circle" />
                    </div>
                    <img
                      className="landing-v2-problem-solution__phone"
                      src={problemSolutionSection.solutionImageSrc}
                      alt={problemSolutionSection.solutionImageAlt}
                    />
                  </div>
                  <div className="landing-v2-problem-solution__col landing-v2-problem-solution__col--solution">
                    <div className="landing-v2-problem-solution__body">
                      <h3 className="landing-v2-problem-solution__label">Solution</h3>
                      <ul className="landing-v2-problem-solution__list">
                        {problemSolutionSection.solution.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              </div>
            </div>
          </section>

          <section id="features" className="landing-v2-features" aria-labelledby="features-title">
            <div className="landing-page-width landing-page-width--ruled">
              <header className="landing-v2-features__header">
                <h2 id="features-title" className="landing-v2-features__title">
                  Everything you need. Nothing you don&apos;t.
                </h2>
                <p className="landing-v2-features__lead">
                  Portfolio tracking made clear, so you can focus on strategies and
                  decisions — not spreadsheets.
                </p>
              </header>
              <div className="landing-v2-features__grid">
                {featureCards.map((card, index) => (
                  <Reveal
                    as="article"
                    key={card.id}
                    className="landing-v2-feature-card"
                    delay={(index % 2) * 100}
                  >
                    <div className="landing-v2-feature-card__visual">
                      <img src={card.visualSrc} alt={card.visualAlt} />
                    </div>
                    <div className="landing-v2-feature-card__body">
                      <h3>{card.title}</h3>
                      <p>{card.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <div className="landing-v2-section-cta">
                <button
                  type="button"
                  className="landing-cobalt-pill landing-cobalt-pill--secondary"
                  onClick={() => {
                    const el = document.getElementById("how-it-works");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                >
                  See how it works
                </button>
              </div>
            </div>
          </section>

          <section id="how-it-works" className="landing-v2-how" aria-labelledby="how-it-works-title">
            <div className="landing-page-width landing-page-width--ruled">
              <h2 id="how-it-works-title" className="landing-v2-how__title">
                How it works
              </h2>
              <div className="landing-v2-how__steps">
                {howItWorksSteps.map((item, index) => (
                  <Reveal
                    as="article"
                    key={item.step}
                    className="landing-v2-how__step"
                    delay={index * 100}
                  >
                    <span className="landing-v2-how__step-num">{item.step}</span>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>

          <section id="faq" className="landing-v2-faq" aria-labelledby="faq-title">
            <div className="landing-page-width landing-page-width--ruled">
              <h2 id="faq-title" className="landing-v2-faq__title">
                FAQ
              </h2>
              <div className="landing-v2-faq__list">
                {faqItems.map((item, index) => (
                  <Reveal
                    as="details"
                    key={item.question}
                    className="landing-v2-faq__item"
                    delay={Math.min(index, 4) * 60}
                  >
                    <summary>{item.question}</summary>
                    <p>{item.answer}</p>
                  </Reveal>
                ))}
              </div>
              <div className="landing-v2-section-cta landing-v2-section-cta--faq">
                <DownloadAppCta className="landing-cobalt-pill landing-cobalt-pill--primary">
                  Get InvestX free
                </DownloadAppCta>
                <p className="landing-v2-hero__meta">
                  Free plan available · No brokerage account required
                </p>
              </div>
            </div>
          </section>

          {/* In-page footer (Cobalt-style legal strip) */}
          <div className="landing-cobalt-pagefoot">
            <div className="landing-page-width landing-page-width--ruled-top">
              <div className="landing-cobalt-pagefoot__inner">
                <span>© {new Date().getFullYear()} InvestX</span>
                <div className="landing-cobalt-pagefoot__links">
                  <button type="button" onClick={() => navigate("/privacy")}>
                    Privacy
                  </button>
                  <button type="button" onClick={() => navigate("/support")}>
                    Support
                  </button>
                  <button type="button" onClick={() => navigate("/terms")}>
                    Terms
                  </button>
                  <button type="button" onClick={() => navigate("/delete-account")}>
                    Delete account
                  </button>
                  <button type="button" onClick={openPinModal}>
                    Admin
                  </button>
                </div>
              </div>
              <p className="landing-cobalt-pagefoot__disclaimer">
                <strong>Illustrative marketing only.</strong> Not investment, tax, legal,
                or financial advice. InvestX is software for tracking and context; it
                does not provide recommendations or execute trades. App Store
                availability and features may vary by region and release.
              </p>
            </div>
          </div>
        </main>
      ) : (
        children
      )}
    </div>
  );
};

export default LandingPage;
